import React, { useEffect, useState, useRef } from 'react';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  Divider,
  Button,
  Card,
  CardContent,
  IconButton,
  Chip,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Avatar,
  ListItemAvatar,
} from '@mui/material';
import {
  Mail as MailIcon,
  Schedule as ScheduleIcon,
  EventNote as EventNoteIcon,
  AssignmentTurnedIn as PermanencesIcon,
  Person as PersonIcon,
  AccessTime as AccessTimeIcon,
  SupervisorAccount as SupervisorIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { doc, getDoc, collection, query, where, getDocs, orderBy, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase/config';
import Messages from './Messages';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const PersonnelDashboard = () => {
  const navigate = useNavigate();
  const { userData, currentUser } = useAuth();
  const [schedule, setSchedule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showMessages, setShowMessages] = useState(false);
  const [absenceRequests, setAbsenceRequests] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const notificationsUnsubscribeRef = useRef(null);

  // Fonction pour formater l'heure
  const formatTime = (time) => {
    if (!time) return '--:--';
    if (typeof time === 'string') return time;
    return time;
  };

  // Récupérer l'emploi du temps et les demandes d'absence
  useEffect(() => {
    const fetchData = async () => {
      try {
        if (!currentUser?.uid) return;
        
        // Récupérer l'emploi du temps
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setSchedule(userData.schedule || {});
        }

        // Récupérer les demandes d'absence
        const absencesQuery = query(
          collection(db, 'absences'),
          where('personnelId', '==', currentUser.uid)
        );
        const absencesSnapshot = await getDocs(absencesQuery);
        const absencesData = absencesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setAbsenceRequests(absencesData);

      } catch (error) {
        console.error('Erreur lors de la récupération des données:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [currentUser]);

  // Obtenir le jour actuel en français
  const getCurrentDay = () => {
    const days = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
    const today = new Date().getDay();
    return days[today];
  };

  // Ordre des jours de la semaine
  const dayOrder = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];

  // Fonction pour trier les jours
  const sortDays = (schedule) => {
    if (!schedule) return [];
    return dayOrder
      .map(day => [day, schedule[day] || { active: false }]);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'acceptée':
        return 'success';
      case 'refusée':
        return 'error';
      default:
        return 'warning';
    }
  };

  // Cleanup listener
  useEffect(() => {
    return () => {
      if (notificationsUnsubscribeRef.current) {
        notificationsUnsubscribeRef.current();
      }
    };
  }, []);

  // Listen for notifications
  useEffect(() => {
    if (!userData?.establishmentId || !currentUser?.uid) return;

    const notificationsRef = collection(db, 'messages');
    const notificationsQuery = query(
      notificationsRef,
      where('recipientId', '==', currentUser.uid),
      where('read', '==', false),
      orderBy('createdAt', 'desc')
    );

    notificationsUnsubscribeRef.current = onSnapshot(notificationsQuery, (snapshot) => {
      const newNotifications = snapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
        .filter(msg => msg.establishmentId === userData.establishmentId);
      setNotifications(newNotifications);
    });

    return () => {
      if (notificationsUnsubscribeRef.current) {
        notificationsUnsubscribeRef.current();
      }
    };
  }, [userData, currentUser]);

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Grid container spacing={3}>
        {/* Welcome Message */}
        <Grid item xs={12}>
          <Paper 
            sx={{ 
              p: 3, 
              background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
              color: 'white'
            }}
          >
            <Typography variant="h4" gutterBottom>
              Bienvenue {userData?.firstName} {userData?.lastName}
            </Typography>
            <Typography variant="body1">
              Département : {userData?.department}
            </Typography>
          </Paper>
        </Grid>

        {/* Quick Actions */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
              Actions Rapides
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Card 
                  sx={{ 
                    height: '100%', 
                    cursor: 'pointer',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 4,
                    }
                  }}
                  onClick={() => navigate('/personnel/messages')}
                >
                  <CardContent sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center',
                    textAlign: 'center'
                  }}>
                    <MailIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
                    <Typography variant="subtitle1">Messagerie</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6}>
                <Card 
                  sx={{ 
                    height: '100%', 
                    cursor: 'pointer',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 4,
                    }
                  }}
                  onClick={() => navigate('/personnel/timetable')}
                >
                  <CardContent sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center',
                    textAlign: 'center'
                  }}>
                    <ScheduleIcon color="secondary" sx={{ fontSize: 40, mb: 1 }} />
                    <Typography variant="subtitle1">Emploi du temps</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6}>
                <Card 
                  sx={{ 
                    height: '100%', 
                    cursor: 'pointer',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 4,
                    }
                  }}
                  onClick={() => navigate('/personnel/leave-request')}
                >
                  <CardContent sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center',
                    textAlign: 'center'
                  }}>
                    <EventNoteIcon color="info" sx={{ fontSize: 40, mb: 1 }} />
                    <Typography variant="subtitle1">Demande d'absence</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6}>
                <Card
                  sx={{
                    height: '100%',
                    cursor: 'pointer',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 4,
                    }
                  }}
                  onClick={() => navigate('/personnel/permanences')}
                >
                  <CardContent sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center'
                  }}>
                    <PermanencesIcon color="success" sx={{ fontSize: 40, mb: 1 }} />
                    <Typography variant="subtitle1">Permanences</Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Today's Schedule */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <AccessTimeIcon sx={{ mr: 1 }} color="primary" />
              <Typography variant="h6">
                Mon Emploi du temps
              </Typography>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Jour</TableCell>
                    <TableCell>Matin</TableCell>
                    <TableCell>Après-midi</TableCell>
                    <TableCell>Statut</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortDays(userData?.schedule).map(([day, schedule]) => (
                    <TableRow key={day} sx={{
                      backgroundColor: day === getCurrentDay() ? 'rgba(33, 150, 243, 0.08)' : 'inherit'
                    }}>
                      <TableCell component="th" scope="row">
                        {day}
                      </TableCell>
                      <TableCell>
                        {schedule.withBreak
                          ? `${formatTime(schedule.morning?.start)} - ${formatTime(schedule.morning?.end)}`
                          : `${formatTime(schedule.continuous?.start)} - ${formatTime(schedule.continuous?.end)}`
                        }
                      </TableCell>
                      <TableCell>
                        {schedule.withBreak
                          ? `${formatTime(schedule.afternoon?.start)} - ${formatTime(schedule.afternoon?.end)}`
                          : '-'
                        }
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={schedule.active ? "Actif" : "Inactif"}
                          color={schedule.active ? "success" : "default"}
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                size="small"
                color="primary"
                onClick={() => navigate('/personnel/timetable')}
                endIcon={<ScheduleIcon />}
              >
                Voir l'emploi du temps complet
              </Button>
            </Box>
          </Paper>
        </Grid>

        {/* Messages Section */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <MailIcon sx={{ mr: 1 }} color="primary" />
              <Typography variant="h6">
                Messages non lus ({notifications.length})
              </Typography>
            </Box>
            {notifications.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                Aucun message non lu
              </Typography>
            ) : (
              <>
                <List>
                  {notifications.slice(0, 3).map((notification) => (
                    <React.Fragment key={notification.id}>
                      <ListItem
                        sx={{
                          backgroundColor: 'rgba(33, 150, 243, 0.08)',
                          borderRadius: 1,
                          mb: 1
                        }}
                      >
                        <ListItemAvatar>
                          <Avatar>
                            {notification.senderRole === 'admin' ? (
                              <SupervisorIcon />
                            ) : (
                              <PersonIcon />
                            )}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={notification.senderName}
                          secondary={
                            <>
                              <Typography
                                component="span"
                                variant="body2"
                                color="text.primary"
                              >
                                {notification.content.length > 50
                                  ? notification.content.substring(0, 50) + '...'
                                  : notification.content}
                              </Typography>
                              <br />
                              <Typography
                                component="span"
                                variant="caption"
                                color="text.secondary"
                              >
                                {format(notification.createdAt.toDate(), 'dd/MM/yyyy HH:mm', { locale: fr })}
                              </Typography>
                            </>
                          }
                        />
                      </ListItem>
                      <Divider variant="inset" component="li" />
                    </React.Fragment>
                  ))}
                </List>
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                  <Button
                    size="small"
                    color="primary"
                    onClick={() => navigate('/personnel/messages')}
                    endIcon={<MailIcon />}
                  >
                    {notifications.length > 3 
                      ? `Voir tous les messages (${notifications.length})`
                      : 'Voir tous les messages'
                    }
                  </Button>
                </Box>
              </>
            )}
          </Paper>
        </Grid>

        {/* Recent Absence Requests */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Dernières demandes d'absence
            </Typography>
            <Grid container spacing={2}>
              {absenceRequests.slice(0, 3).map((request) => (
                <Grid item xs={12} md={4} key={request.id}>
                  <Card>
                    <CardContent>
                      <Typography variant="subtitle1" gutterBottom>
                        {request.type}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Du: {new Date(request.dateDebut.seconds * 1000).toLocaleDateString('fr-FR')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Au: {new Date(request.dateFin.seconds * 1000).toLocaleDateString('fr-FR')}
                      </Typography>
                      <Box sx={{ mt: 1 }}>
                        <Chip
                          label={request.statut}
                          color={getStatusColor(request.statut)}
                          size="small"
                        />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Grid>
      </Grid>

      {/* Messages Dialog */}
      {showMessages && <Messages onClose={() => setShowMessages(false)} />}
    </Container>
  );
};

export default PersonnelDashboard;
